import { FC, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Divider, Dropdown, Menu, Space } from 'antd';
import { AxiosResponse } from 'axios';
import { Tags as TagsIcon } from 'lucide-react';
import { MdAddChart } from 'react-icons/md';
import { TbCloudBolt, TbCopy, TbCrosshair, TbInfoCircle, TbPlus, TbProgressCheck, TbX } from 'react-icons/tb';
import { useNavigate } from 'react-router-dom';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useWorkspaceResourceCreateAccess } from '@/hooks/useWorkspaceResourceCreateAccess';
import AlertEditorModal from '@/layouts/02-Authenticated/layout/03-Content/routes/12-Settings/layout/02-Content/routes/Account/03-Notifications/components/AlertEditorModal';
import { useAlertsCatalog } from '@/queries';
import {
    CreateAlertIcon,
    DropdownMenuLabel,
    DropdownOptionContent,
    DropdownPanel,
    DropdownPanelMenu,
    PlanTag,
    SmallTooltip,
} from '@/shared';
import { DropdownButtonIcon } from '@/shared/components/dropdowns/DropdownButton';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useInvestmentStore } from '@/stores';
import { SetStateMethod } from '@/types';
import { nanoId } from '@/utilities';

import BulkCompanyAddModal from './BulkCompanyAddModal';
import SyncToDataSourcesModal from './SyncToConnectedAccountModal';

export interface CompanyActionResult {
    entityId?: string;
    resultType?: string;
    selectionId?: string;
    symbol?: string;
    [key: string]: unknown;
}

interface ActionsRowProps {
    results: CompanyActionResult[];
    resultsSelected: Set<string>;
    setCheckAll: SetStateMethod<boolean>;
    setIndeterminate: SetStateMethod<boolean>;
    setResultsSelected: SetStateMethod<Set<string>>;
}

const ActionsRow: FC<ActionsRowProps> = ({
    results,
    resultsSelected,
    setCheckAll,
    setIndeterminate,
    setResultsSelected,
}): JSX.Element => {
    const [loading, setLoading] = useState(false);
    const [addModalKind, setAddModalKind] = useState<'tag' | 'watchlist'>();
    const [createAlertOpen, setCreateAlertOpen] = useState(false);
    const [syncToSpreadsheetModalOpen, setSyncToSpreadsheetModalOpen] = useState(false);
    const navigate = useNavigate();
    const { token } = useAntDesignTheme();
    const { hasFeature } = useEntitlements();
    const { investments } = useInvestmentStore();
    const createInvestmentAccess = useWorkspaceResourceCreateAccess('investments', investments?.length ?? 0);
    const canUseAlerts = hasFeature('alerts');
    const alertsCatalog = useAlertsCatalog(canUseAlerts);

    const selectedResults = useMemo(
        () =>
            results.filter((result) => {
                const id = result.selectionId ?? result.entityId ?? result.symbol;

                return Boolean(id && resultsSelected.has(id));
            }),
        [results, resultsSelected],
    );
    const selectedSymbols = useMemo(
        () =>
            Array.from(
                new Set(
                    selectedResults
                        .map(
                            (result) => result.symbol ?? (result.enrichment as { symbol?: string } | undefined)?.symbol,
                        )
                        .filter((symbol): symbol is string => Boolean(symbol)),
                ),
            ),
        [selectedResults],
    );
    const canUsePublicCompanyActions = selectedResults.length > 0 && selectedSymbols.length === selectedResults.length;
    const investmentUsage = createInvestmentAccess.usageState;
    const exceedsInvestmentLimit =
        investmentUsage.remaining !== null && selectedResults.length > investmentUsage.remaining;

    const hasConnectedCompanySelected = selectedResults.some((result) => result.resultType === 'nylasCompany');

    const handleCreateInvestments = async () => {
        if (createInvestmentAccess.disabled || exceedsInvestmentLimit) {
            return;
        }

        try {
            setLoading(true);

            await authorizedAxiosInstance
                .post('/api/bulk/investments', {
                    symbols: selectedSymbols.join(','),
                })
                .then((res: AxiosResponse) => {
                    if (res.status === 200) {
                        toast('Investments Created', {
                            description: `${res.data?.length} Investments Created`,
                        });
                        navigate('/investments');
                    }
                })
                .catch((error) => {
                    toast('Unable to Create Investments', {
                        description: 'Please refresh the page and contact support if the issue persists.',
                    });
                    captureException(error);
                })
                .finally(() => setLoading(false));
        } catch (error) {
            setLoading(false);
            captureException(error);
        }
    };

    const copySelectedLinks = async () => {
        try {
            const origin = window.location.origin;
            const links = selectedSymbols.map((symbol) => `${origin}/search/companies/${encodeURIComponent(symbol)}`);

            await navigator.clipboard.writeText(links.join('\n'));
            toast.success(`${links.length.toLocaleString()} company link${links.length === 1 ? '' : 's'} copied.`);
        } catch (error) {
            captureException(error);
            toast.error('Unable to copy the selected company links.');
        }
    };

    const selectedCompanyLabel =
        selectedResults.length === 1
            ? ((selectedResults[0]?.enrichment as { companyName?: string } | undefined)?.companyName ??
              (selectedResults[0]?.companyName as string | undefined) ??
              (selectedResults[0]?.name as string | undefined) ??
              selectedSymbols[0])
            : `${selectedResults.length.toLocaleString()} Companies`;

    return (
        <div style={{ alignItems: 'center', display: 'flex', minWidth: 0, width: '100%' }}>
            {resultsSelected.size > 0 ? (
                <>
                    <span data-selection-control='true' style={{ display: 'inline-flex', flex: 'none' }}>
                        <Space.Compact size='small' style={{ borderRadius: token.borderRadius }}>
                            <Button
                                icon={<TbProgressCheck style={{ verticalAlign: '-0.125rem' }} />}
                                style={{ pointerEvents: 'none' }}
                                type='dashed'
                            >
                                <small style={{ verticalAlign: '0.05rem' }}>
                                    {resultsSelected.size} Result{resultsSelected.size > 1 && 's'} Selected
                                </small>
                            </Button>
                            <Button
                                aria-label='Clear selected results'
                                icon={<TbX style={{ color: token.colorTextTertiary, verticalAlign: '-0.125rem' }} />}
                                onClick={() => {
                                    setResultsSelected(new Set());
                                    setIndeterminate(false);
                                    setCheckAll(false);
                                }}
                                type='dashed'
                            />
                        </Space.Compact>
                    </span>
                    <Divider type='vertical' style={{ borderColor: token.colorBorder, margin: '0 0.5rem' }} />
                    <span data-selection-control='true' style={{ display: 'inline-flex', flex: 'none' }}>
                        <Space.Compact size='small' style={{ borderRadius: token.borderRadius }}>
                            <Dropdown
                                placement='bottomLeft'
                                trigger={['click']}
                                popupRender={() => (
                                    <DropdownPanel
                                        header={
                                            <DropdownMenuLabel icon={<TbPlus size='0.875rem' />}>
                                                Create
                                            </DropdownMenuLabel>
                                        }
                                        width={21 * 16}
                                    >
                                        <DropdownPanelMenu selectable={false}>
                                            <Menu.Item
                                                key='investments'
                                                disabled={
                                                    createInvestmentAccess.disabled ||
                                                    exceedsInvestmentLimit ||
                                                    loading ||
                                                    hasConnectedCompanySelected ||
                                                    !canUsePublicCompanyActions
                                                }
                                                onClick={() => void handleCreateInvestments()}
                                            >
                                                <DropdownOptionContent
                                                    description='Create workspace investments for the selected companies.'
                                                    icon={<MdAddChart size='1rem' />}
                                                    title={`Investment${selectedResults.length === 1 ? '' : 's'}`}
                                                />
                                            </Menu.Item>
                                            <Menu.Item
                                                key='alert'
                                                disabled={
                                                    !canUseAlerts ||
                                                    !alertsCatalog.data ||
                                                    !canUsePublicCompanyActions ||
                                                    selectedSymbols.length > 50
                                                }
                                                onClick={() => {
                                                    if (canUseAlerts) {
                                                        setCreateAlertOpen(true);
                                                    }
                                                }}
                                            >
                                                <DropdownOptionContent
                                                    description='Monitor material events across the selected companies.'
                                                    icon={<CreateAlertIcon />}
                                                    title={
                                                        <span style={{ alignItems: 'center', display: 'inline-flex' }}>
                                                            Alert
                                                            <PlanTag
                                                                plan='enterprise'
                                                                style={{ marginLeft: '0.5rem' }}
                                                            />
                                                        </span>
                                                    }
                                                />
                                            </Menu.Item>
                                        </DropdownPanelMenu>
                                    </DropdownPanel>
                                )}
                            >
                                <Button loading={loading} size='small' type='dashed'>
                                    <TbPlus size='0.875rem' />
                                    <span>Create…</span>
                                    <DropdownButtonIcon />
                                </Button>
                            </Dropdown>
                            <Dropdown
                                placement='bottomLeft'
                                trigger={['click']}
                                popupRender={() => (
                                    <DropdownPanel
                                        header={
                                            <DropdownMenuLabel icon={<TbCloudBolt size='0.875rem' />}>
                                                Sync
                                            </DropdownMenuLabel>
                                        }
                                        width={21 * 16}
                                    >
                                        <DropdownPanelMenu selectable={false}>
                                            <Menu.Item
                                                key='data-sources'
                                                disabled={loading || hasConnectedCompanySelected}
                                                onClick={() => setSyncToSpreadsheetModalOpen(true)}
                                            >
                                                <DropdownOptionContent
                                                    description='Send selected companies to a connected data source.'
                                                    icon={<TbCloudBolt size='1rem' />}
                                                    title='Data Sources'
                                                />
                                            </Menu.Item>
                                        </DropdownPanelMenu>
                                    </DropdownPanel>
                                )}
                            >
                                <Button size='small' type='dashed'>
                                    <TbCloudBolt size='0.875rem' />
                                    <span>Sync…</span>
                                    <DropdownButtonIcon />
                                </Button>
                            </Dropdown>
                            <Dropdown
                                placement='bottomLeft'
                                trigger={['click']}
                                popupRender={() => (
                                    <DropdownPanel
                                        header={
                                            <DropdownMenuLabel icon={<TbPlus size='0.875rem' />}>Add</DropdownMenuLabel>
                                        }
                                        width={21 * 16}
                                    >
                                        <DropdownPanelMenu selectable={false}>
                                            <Menu.Item key='tag' onClick={() => setAddModalKind('tag')}>
                                                <DropdownOptionContent
                                                    description='Attach an existing classification to every selected company.'
                                                    icon={<TagsIcon size='1rem' />}
                                                    title='Tag'
                                                />
                                            </Menu.Item>
                                            <Menu.Item key='watchlist' onClick={() => setAddModalKind('watchlist')}>
                                                <DropdownOptionContent
                                                    description='Add every selected company to an existing watchlist.'
                                                    icon={<TbCrosshair size='1rem' />}
                                                    title='Watchlist'
                                                />
                                            </Menu.Item>
                                        </DropdownPanelMenu>
                                    </DropdownPanel>
                                )}
                            >
                                <Button size='small' type='dashed'>
                                    <TbPlus size='0.875rem' />
                                    <span>Add…</span>
                                    <DropdownButtonIcon />
                                </Button>
                            </Dropdown>
                            <SmallTooltip title='Copy company links'>
                                <Button
                                    aria-label='Copy company links'
                                    disabled={!selectedSymbols.length}
                                    icon={<TbCopy size='0.875rem' />}
                                    size='small'
                                    type='dashed'
                                    onClick={() => void copySelectedLinks()}
                                />
                            </SmallTooltip>
                        </Space.Compact>
                    </span>
                </>
            ) : (
                <small
                    style={{
                        alignItems: 'center',
                        color: token.colorTextTertiary,
                        display: 'inline-flex',
                    }}
                >
                    <TbInfoCircle style={{ marginRight: '0.375rem', verticalAlign: '-0.125rem' }} />
                    Select items from results for more actions
                </small>
            )}
            <SyncToDataSourcesModal
                open={syncToSpreadsheetModalOpen}
                results={results}
                resultsSelected={resultsSelected}
                setCheckAll={setCheckAll}
                setIndeterminate={setIndeterminate}
                setOpen={setSyncToSpreadsheetModalOpen}
                setResultsSelected={setResultsSelected}
            />
            {addModalKind ? (
                <BulkCompanyAddModal
                    kind={addModalKind}
                    open
                    results={selectedResults}
                    onClose={() => setAddModalKind(undefined)}
                />
            ) : null}
            {canUseAlerts && createAlertOpen && alertsCatalog.data ? (
                <AlertEditorModal
                    catalog={alertsCatalog.data}
                    initialInput={{
                        category: 'companies',
                        deliveryMethods: ['inApp'],
                        description: `Monitor material events and changes for ${selectedCompanyLabel}.`,
                        enabled: true,
                        name: `${selectedCompanyLabel} Alert`,
                        query: {
                            conditions: [
                                {
                                    field: 'symbol',
                                    id: nanoId(12, true),
                                    operator: selectedSymbols.length === 1 ? 'equals' : 'in',
                                    value: selectedSymbols.length === 1 ? selectedSymbols[0] : selectedSymbols,
                                },
                            ],
                            match: 'all',
                            version: 1,
                        },
                    }}
                    initialView='create'
                    open
                    onClose={() => setCreateAlertOpen(false)}
                />
            ) : null}
        </div>
    );
};

export default ActionsRow;
